import { captureMessage } from "@sentry/nextjs";
import { format, isValid, parse } from "date-fns";

import { getApiBasePath } from "@/app/base-path";
import { SMS_CONSENT_QUESTIONS } from "@/constants";
import { toE164UsPhone } from "@/lib/utils/format-phone-number";
import { getSessionId } from "@/lib/utils/user-session-id";

const FORM_EVENT_TIMEOUT_MS = 10_000;

type FormEventInput = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  dateOfBirth: string;
  reasonForVisit: string;
  phoneNumber: string;
  // Undefined when the consent checkboxes weren't shown, so no consent is recorded either way.
  smsMarketing?: boolean;
  smsTransactional?: boolean;
};

export async function formEvent({
  firstName,
  lastName,
  emailAddress,
  dateOfBirth,
  reasonForVisit,
  phoneNumber,
  smsMarketing,
  smsTransactional,
}: FormEventInput): Promise<void> {
  try {
    // An unparseable DOB must not cost us the whole lead: drop the single question,
    // report it, and still post the event.
    const parsedDateOfBirth = parse(dateOfBirth, "MM/dd/yyyy", new Date());
    if (!isValid(parsedDateOfBirth)) {
      captureMessage("form_event.date_of_birth.unparseable", { level: "warning" });
    }

    const e164PhoneNumber = toE164UsPhone(phoneNumber);
    if (!e164PhoneNumber) {
      captureMessage("form_event.phone_number.unparseable", { level: "warning" });
    }

    const body = JSON.stringify({
      form: {
        brand: "Aspen Dental",
        form_title: "aspen-scheduler",
        product_line: "Aspen Dental",
        form_description: "Aspen Scheduler",
        form_type: "Quiz",
        form_channel: "Web",
        session_id: getSessionId(),
        form_questions: [
          { question: "first_name", answer: firstName, unique_label: "first_name" },
          { question: "last_name", answer: lastName, unique_label: "last_name" },
          { question: "email_address", answer: emailAddress, unique_label: "email_address" },
          ...(isValid(parsedDateOfBirth)
            ? [
                {
                  question: "date_of_birth",
                  answer: format(parsedDateOfBirth, "yyyy-MM-dd"),
                  unique_label: "date_of_birth",
                },
              ]
            : []),
          {
            question: "reason_for_visit",
            answer: reasonForVisit,
            unique_label: "reason_for_visit",
          },
          ...(e164PhoneNumber
            ? [{ question: "phone_number", answer: e164PhoneNumber, unique_label: "phone_number" }]
            : []),
          ...(typeof smsMarketing === "boolean"
            ? [
                {
                  question: SMS_CONSENT_QUESTIONS.marketing,
                  answer: String(smsMarketing),
                  unique_label: SMS_CONSENT_QUESTIONS.marketing,
                },
              ]
            : []),
          ...(typeof smsTransactional === "boolean"
            ? [
                {
                  question: SMS_CONSENT_QUESTIONS.transactional,
                  answer: String(smsTransactional),
                  unique_label: SMS_CONSENT_QUESTIONS.transactional,
                },
              ]
            : []),
        ],
      },
    });

    await fetch(`${getApiBasePath()}/api/formEvents/`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body,
      signal: AbortSignal.timeout(FORM_EVENT_TIMEOUT_MS),
    });
  } catch {
    // CRM form-event delivery is non-critical to the scheduling journey.
  }
}
